import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sliders } from "lucide-react";

export default function Settings() {
  const [refPct, setRefPct] = useState(20);
  const [closePct, setClosePct] = useState(80);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setRefPct(Math.round(r.data.referral_share * 100));
      setClosePct(Math.round(r.data.closure_share * 100));
    });
  }, []);

  const onRefChange = (v) => {
    const n = Math.max(0, Math.min(100, parseFloat(v) || 0));
    setRefPct(n);
    setClosePct(100 - n);
  };

  const onCloseChange = (v) => {
    const n = Math.max(0, Math.min(100, parseFloat(v) || 0));
    setClosePct(n);
    setRefPct(100 - n);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        referral_share: refPct / 100,
        closure_share: closePct / 100,
      });
      toast.success("Attribution split updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const total = refPct + closePct;

  return (
    <Shell title="Settings" subtitle="Configure attribution split between deal closure and customer referral.">
      <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white max-w-2xl">
        <div className="flex items-center gap-2 mb-4">
          <Sliders size={16} strokeWidth={1.5} className="text-zinc-500" />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Attribution Split</div>
            <h2 className="text-xl font-bold tracking-tight font-heading">Deal Closure vs Customer Referral</h2>
          </div>
        </div>
        <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
          When the customer is referred by one salesperson and the deal is closed by another, the invoice value &
          margin are split between them using these percentages. Same person on both fields = 100%.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="font-semibold">Customer Referral %</Label>
            <Input type="number" min="0" max="100" value={refPct} onChange={(e) => onRefChange(e.target.value)} data-testid="referral-share-input" />
          </div>
          <div>
            <Label className="font-semibold">Deal Closure %</Label>
            <Input type="number" min="0" max="100" value={closePct} onChange={(e) => onCloseChange(e.target.value)} data-testid="closure-share-input" />
          </div>
        </div>

        <div className="mt-4 flex h-3 rounded-md overflow-hidden border border-zinc-200">
          <div className="bg-amber-500 transition-all" style={{ width: `${refPct}%` }} title={`Referral ${refPct}%`} />
          <div className="bg-zinc-950 transition-all" style={{ width: `${closePct}%` }} title={`Closure ${closePct}%`} />
        </div>
        <div className="flex justify-between text-xs text-zinc-500 mt-1.5">
          <span>Referral {refPct}%</span>
          <span>Closure {closePct}%</span>
        </div>

        {total !== 100 && (
          <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
            Total must equal 100% (currently {total}%).
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button disabled={saving || total !== 100} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="save-settings-button">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </Card>
    </Shell>
  );
}
